<template>
  <div
    class="modal modal-open items-start !animate-none overflow-y-auto"
  >
    <div
      class="absolute top-0 bottom-0 right-0 left-0"
      @click.prevent="$emit('close')"
    />
    <div class="modal-box pt-4 pb-6 px-6 mt-20 max-h-none w-full max-w-xl ed-condition-modal">
      <div class="flex justify-between items-center border-b pb-2 mb-2 font-medium">
        <span class="modal-title">
          {{ t('condition') }} - {{ (defaultField ? (defaultField.title || item.title || item.name) : item.name) || buildDefaultName(item) }}
        </span>
        <a
          href="#"
          class="text-xl modal-close-button"
          @click.prevent="$emit('close')"
        >&times;</a>
      </div>
      <div>
        <form @submit.prevent="validateSaveAndClose">
          <div class="my-4">
            <div
              v-for="(condition, cindex) in conditions"
              :key="cindex"
              class="ed-condition-row space-y-4 relative"
            >
              <div
                v-if="cindex > 0"
                class="divider -mb-2 mx-1"
              >
                <button
                  type="button"
                  class="btn btn-xs btn-primary w-24"
                  @click.prevent="condition.operation === 'or' ? delete condition.operation : condition.operation = 'or'"
                >
                  {{ condition.operation === 'or' ? t('or') : t('and') }}
                </button>
              </div>
              <div
                v-if="conditions.length > 1"
                class="flex justify-between mx-1"
              >
                <label class="text-sm">
                  {{ t('condition') }} {{ cindex + 1 }}
                </label>
                <a
                  href="#"
                  class="link text-sm"
                  @click.prevent="conditions.splice(cindex, 1)"
                > {{ t('remove') }}</a>
              </div>
              <select
                class="select select-bordered select-sm w-full bg-white h-11 pl-4 text-base font-normal"
                :class="{ 'text-gray-300': !condition.field_uuid }"
                required
                @change="onFieldChange(condition, $event.target.value)"
              >
                <option
                  value=""
                  disabled
                  :selected="!condition.field_uuid"
                >
                  {{ t('select_field_') }}
                </option>
                <option
                  v-for="f in fields"
                  :key="f.uuid"
                  :value="f.uuid"
                  class="text-base-content"
                  :selected="condition.field_uuid === f.uuid"
                >
                  {{ f.name || buildDefaultName(f) }}
                </option>
              </select>
              <select
                v-model="condition.action"
                class="select select-bordered select-sm w-full h-11 pl-4 text-base font-normal"
                :class="{ 'bg-white': condition.field_uuid, 'bg-base-300': !condition.field_uuid }"
                :required="!!condition.field_uuid"
                @change="onActionChange(condition)"
              >
                <option
                  v-for="action in conditionActions(condition)"
                  :key="action"
                  :value="action"
                >
                  {{ t(action) }}
                </option>
              </select>
              <select
                v-if="['radio', 'select', 'multiple'].includes(conditionField(condition)?.type) && conditionField(condition)?.options"
                class="select select-bordered select-sm w-full bg-white h-11 pl-4 text-base font-normal"
                :class="{ 'text-gray-300': !condition.value }"
                required
                @change="condition.value = $event.target.value"
              >
                <option
                  value=""
                  disabled
                  selected
                >
                  {{ t('select_value_') }}
                </option>
                <option
                  v-for="(option, index) in conditionField(condition).options"
                  :key="option.uuid"
                  :value="option.uuid"
                  :selected="condition.value === option.uuid"
                  class="text-base-content"
                >
                  {{ option.value || `${t('option')} ${index + 1}` }}
                </option>
              </select>
              <ConditionFormulaBuilder
                v-else-if="showNumericValueInput(condition)"
                :value-type="conditionValueType(condition)"
                :fixed-value="condition.value || ''"
                :formula-value="conditionFormulaDraft(condition)"
                :number-fields="numberFieldsForCondition(condition)"
                :build-default-name="buildDefaultName"
                @update:value-type="setConditionValueType(condition, $event)"
                @update:fixed-value="condition.value = $event"
                @update:formula-value="setConditionFormulaDraft(condition, $event)"
              />
            </div>
          </div>
          <a
            href="#"
            class="inline float-right link text-right mb-3 px-2"
            @click.prevent="conditions.push(createEmptyCondition())"
          > + {{ t('add_condition') }}</a>
          <button class="base-button w-full mt-2 modal-save-button">
            {{ t('save') }}
          </button>
        </form>
        <div
          v-if="item.conditions?.[0]?.field_uuid"
          class="text-center w-full mt-4"
        >
          <button
            type="button"
            class="link"
            @click="[conditions = [], delete item.conditions, validateSaveAndClose()]"
          >
            {{ t('remove_condition') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConditionFormulaBuilder from './condition_formula_builder'
import {
  getNumberFields,
  humanizeFormula,
  normalizeFormula,
  formulaHasMissingFields
} from '../lib/formula_utils'

export default {
  name: 'ConditionModal',
  components: {
    ConditionFormulaBuilder
  },
  inject: ['t', 'template'],
  props: {
    item: {
      type: Object,
      required: true
    },
    defaultField: {
      type: Object,
      required: false,
      default: null
    },
    buildDefaultName: {
      type: Function,
      required: true
    },
    excludeFieldUuids: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['close', 'save'],
  data () {
    const conditions = this.item.conditions?.[0]
      ? JSON.parse(JSON.stringify(this.item.conditions))
      : [this.createEmptyCondition()]

    return {
      conditions,
      formulaDrafts: this.buildFormulaDrafts(conditions)
    }
  },
  computed: {
    excludeTypes () {
      return ['heading', 'strikethrough']
    },
    fields () {
      if (this.item.submitter_uuid) {
        return this.template.fields.reduce((acc, f) => {
          if (f !== this.item && !this.excludeTypes.includes(f.type) && !this.excludeFieldUuids.includes(f.uuid) && (!f.conditions?.length || !f.conditions.find((c) => c.field_uuid === this.item.uuid))) {
            acc.push(f)
          }

          return acc
        }, [])
      } else {
        return this.template.fields.filter((f) => !this.excludeFieldUuids.includes(f.uuid))
      }
    },
    allNumberFields () {
      return getNumberFields(this.template, { excludeField: this.item, excludeUuids: this.excludeFieldUuids })
    }
  },
  created () {
    this.item.conditions ||= []
  },
  methods: {
    createEmptyCondition () {
      return {
        value_type: 'fixed'
      }
    },
    buildFormulaDrafts (conditions) {
      return conditions.reduce((acc, condition, index) => {
        if (condition.value_type === 'formula' && condition.value_formula) {
          acc[index] = humanizeFormula(condition.value_formula, this.allNumberFields, this.buildDefaultName)
        }

        return acc
      }, {})
    },
    conditionField (condition) {
      return this.fields.find((f) => f.uuid === condition.field_uuid)
    },
    conditionActions (condition) {
      return this.fieldActions(this.conditionField(condition))
    },
    fieldActions (field) {
      const actions = []

      if (!field) {
        return actions
      }

      if (field.type === 'checkbox') {
        actions.push('checked', 'unchecked')
      } else if (['radio', 'select'].includes(field.type)) {
        actions.push('equal', 'not_equal')
      } else if (['multiple'].includes(field.type)) {
        actions.push('contains', 'does_not_contain')
      } else if (field.type === 'number') {
        actions.push('not_empty', 'empty', 'equal', 'not_equal', 'greater_than', 'less_than')
      } else {
        actions.push('not_empty', 'empty')
      }

      return actions
    },
    showNumericValueInput (condition) {
      const field = this.conditionField(condition)

      return field?.type === 'number' && ['equal', 'not_equal', 'greater_than', 'less_than'].includes(condition.action)
    },
    conditionValueType (condition) {
      return condition.value_type === 'formula' ? 'formula' : 'fixed'
    },
    conditionFormulaDraft (condition) {
      const index = this.conditions.indexOf(condition)

      return this.formulaDrafts[index] || ''
    },
    setConditionFormulaDraft (condition, value) {
      const index = this.conditions.indexOf(condition)

      this.formulaDrafts[index] = value
    },
    setConditionValueType (condition, valueType) {
      condition.value_type = valueType

      if (valueType === 'fixed') {
        delete condition.value_formula
      } else {
        delete condition.value
      }
    },
    numberFieldsForCondition (condition) {
      const selectedField = this.conditionField(condition)

      return this.allNumberFields.filter((field) => field.uuid !== selectedField?.uuid)
    },
    onFieldChange (condition, fieldUuid) {
      condition.field_uuid = fieldUuid
      delete condition.value
      delete condition.value_formula
      condition.value_type = 'fixed'
      condition.action = this.conditionActions(condition)[0]

      const index = this.conditions.indexOf(condition)

      delete this.formulaDrafts[index]
    },
    onActionChange (condition) {
      if (!this.showNumericValueInput(condition)) {
        delete condition.value_formula
        condition.value_type = 'fixed'

        const index = this.conditions.indexOf(condition)

        delete this.formulaDrafts[index]
      }
    },
    prepareConditionForSave (condition) {
      if (!this.showNumericValueInput(condition)) {
        delete condition.value_type
        delete condition.value_formula

        return condition
      }

      if (condition.value_type === 'formula') {
        const index = this.conditions.indexOf(condition)
        const draft = (this.formulaDrafts[index] || '').trim()

        if (!draft) {
          throw new Error(this.t('condition_formula_placeholder'))
        }

        const normalizedFormula = normalizeFormula(draft, this.allNumberFields, this.buildDefaultName)

        if (formulaHasMissingFields(normalizedFormula)) {
          throw new Error(this.t('some_fields_are_missing_in_the_formula'))
        }

        condition.value_formula = normalizedFormula
        delete condition.value
      } else {
        condition.value_type = 'fixed'
        delete condition.value_formula
      }

      return condition
    },
    validateSaveAndClose () {
      try {
        const preparedConditions = this.conditions
          .filter((condition) => condition.field_uuid)
          .map((condition) => this.prepareConditionForSave({ ...condition }))

        if (preparedConditions.length) {
          this.item.conditions = preparedConditions
        } else {
          delete this.item.conditions
        }
      } catch (error) {
        return alert(error.message)
      }

      this.$emit('save')
      this.$emit('close')
    }
  }
}
</script>
